import { useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Dialog,
  DialogBody,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
  Text,
} from '@backstage/ui';
import { stringifyEntityRef } from '@backstage/catalog-model';
import {
  catalogApiRef,
  useAsyncEntity,
  useEntity,
} from '@backstage/plugin-catalog-react';
import { useApi, configApiRef, fetchApiRef } from '@backstage/core-plugin-api';
import styles from './ttlWarningCard.module.css';
import {
  MAX_ADD_DAYS,
  MAX_ADD_HOURS_TOTAL,
  previewExtendedExpiration,
} from './ttlCalendar';

const EXPIRATION_ANNOTATION = 'infrastructure.backstage.io/expiration-date';
const CREATED_AT_ANNOTATION = 'infrastructure.backstage.io/created-at';

const maxHoursForDays = (days: number) =>
  Math.min(23, Math.max(0, MAX_ADD_HOURS_TOTAL - days * 24));

export const ExtendTtlButton = () => {
  const { entity } = useEntity();
  const { refresh } = useAsyncEntity();
  const catalogApi = useApi(catalogApiRef);
  const config = useApi(configApiRef);
  const { fetch } = useApi(fetchApiRef);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [addDays, setAddDays] = useState(1);
  const [addHours, setAddHours] = useState(0);
  // Stays on the card until the catalog catches up: a toast in the corner is easy to
  // miss, and the date here cannot change for a few minutes.
  const [pending, setPending] = useState<{
    label: string | null;
    stalled: boolean;
  } | null>(null);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({ open: false, message: '', severity: 'success' });

  const annotations = entity.metadata.annotations || {};
  const createdBy = annotations['infrastructure.backstage.io/created-by'];
  const templateType = annotations['infrastructure.backstage.io/type'];
  const envId = annotations['infrastructure.backstage.io/env-id'];
  const expirationDate = annotations[EXPIRATION_ANNOTATION];
  const createdAtRaw = annotations[CREATED_AT_ANNOTATION];
  const isInfrastructure = Boolean(templateType && createdBy && envId);

  useEffect(() => {
    if (!snackbar.open) {
      return undefined;
    }
    const id = window.setTimeout(() => {
      setSnackbar(prev => ({ ...prev, open: false }));
    }, 6000);
    return () => window.clearTimeout(id);
  }, [snackbar.open]);

  if (!isInfrastructure) {
    return null;
  }

  const createdBySanitized =
    createdBy?.replace(/^user:[^/]+\//, '') || createdBy;
  const statePrefix = `${templateType}/${createdBySanitized}/${envId}`;

  // Show the resulting date right away: the card still holds the old one until the
  // catalog is re-read, and an unchanged card reads as "nothing happened".
  const newExpirationLabel = () => {
    if (!expirationDate || expirationDate === 'never') {
      return null;
    }
    const current = new Date(expirationDate);
    if (Number.isNaN(current.getTime())) {
      return null;
    }
    const createdAt = createdAtRaw ? new Date(createdAtRaw) : undefined;
    const preview = previewExtendedExpiration({
      currentExpiration: current,
      createdAt,
      addDays,
      addHours,
    });
    return preview.toLocaleString();
  };

  // The workflow commits the new date a few seconds later, so poll the catalog and
  // re-render the card as soon as it lands instead of asking for a page reload.
  const confirmWhenCatalogCatchesUp = async () => {
    const entityRef = stringifyEntityRef(entity);
    for (let attempt = 0; attempt < 60; attempt++) {
      await new Promise(resolve => {
        window.setTimeout(resolve, 5000);
      });
      const fresh = await catalogApi
        .getEntityByRef(entityRef)
        .catch(() => undefined);
      const current = fresh?.metadata.annotations?.[EXPIRATION_ANNOTATION];
      if (current && current !== expirationDate) {
        refresh?.();
        setPending(null);
        const landed = new Date(current);
        setSnackbar({
          open: true,
          message: Number.isNaN(landed.getTime())
            ? 'TTL extended.'
            : `TTL extended to ${landed.toLocaleString()}.`,
          severity: 'success',
        });
        return;
      }
    }
    // Only reached if the run is still queued or failed — do not leave a stale card
    // behind a message that claimed success.
    setPending(prev => (prev ? { ...prev, stalled: true } : prev));
  };

  const handleExtend = async () => {
    if (addDays + addHours <= 0) {
      setSnackbar({
        open: true,
        message: 'Add at least 1 day or 1 hour.',
        severity: 'error',
      });
      return;
    }
    if (addDays * 24 + addHours > MAX_ADD_HOURS_TOTAL) {
      setSnackbar({
        open: true,
        message: `Each extend may add at most ${MAX_ADD_DAYS} days.`,
        severity: 'error',
      });
      return;
    }
    setLoading(true);
    try {
      const backendUrl = config.getString('backend.baseUrl');
      const response = await fetch(
        `${backendUrl}/api/extend-infrastructure/trigger`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            state_prefix: statePrefix,
            add_days: addDays,
            add_hours: addHours,
          }),
        },
      );
      if (response.ok || response.status === 204) {
        // 204 only means GitHub accepted the dispatch, so this stays in progress
        // until the poll below sees the new date in the catalog.
        const newExpiration = newExpirationLabel();
        setPending({ label: newExpiration, stalled: false });
        setOpen(false);
        void confirmWhenCatalogCatchesUp();
      } else {
        const errorText = await response.text();
        throw new Error(
          `Failed to extend TTL: ${response.status} - ${errorText}`,
        );
      }
    } catch (error) {
      setSnackbar({
        open: true,
        message: `Error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <DialogTrigger isOpen={open} onOpenChange={setOpen}>
        <Button variant="secondary" className={styles.extendTrigger}>
          Extend TTL
        </Button>
        <Dialog>
          <DialogHeader>Extend auto-destroy TTL?</DialogHeader>
          <DialogBody>
            <Text variant="body-medium" as="p">
              Adds up to {MAX_ADD_DAYS} days. If the new date lands on a weekend,
              it moves to Monday 09:00.
            </Text>
            <div className={styles.extendFields}>
              <label className={styles.extendLabel}>
                Days
                <input
                  type="number"
                  min={0}
                  max={MAX_ADD_DAYS}
                  value={addDays}
                  onChange={event => {
                    const days = Math.min(
                      MAX_ADD_DAYS,
                      Math.max(0, Number.parseInt(event.target.value, 10) || 0),
                    );
                    setAddDays(days);
                    const maxHours = maxHoursForDays(days);
                    if (addHours > maxHours) {
                      setAddHours(maxHours);
                    }
                  }}
                />
              </label>
              <label className={styles.extendLabel}>
                Hours
                <input
                  type="number"
                  min={0}
                  max={maxHoursForDays(addDays)}
                  value={addHours}
                  disabled={maxHoursForDays(addDays) === 0}
                  onChange={event =>
                    setAddHours(
                      Math.min(
                        maxHoursForDays(addDays),
                        Math.max(0, Number.parseInt(event.target.value, 10) || 0),
                      ),
                    )
                  }
                />
              </label>
            </div>
            {newExpirationLabel() && (
              <Text variant="body-medium" as="p">
                New expiration: <strong>{newExpirationLabel()}</strong>
              </Text>
            )}
          </DialogBody>
          <DialogFooter>
            <Button slot="close" variant="secondary" isDisabled={loading}>
              Cancel
            </Button>
            <Button
              variant="primary"
              isPending={loading}
              onPress={() => void handleExtend()}
            >
              {loading ? 'Extending...' : 'Extend'}
            </Button>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
      {pending && (
        <div className={styles.extendStatus}>
          <Text variant="body-small" as="p">
            {pending.stalled ? (
              <>
                <strong>Extension is taking longer than usual.</strong> The
                workflow has not updated the catalog yet. The dates above change
                as soon as it does — nothing else to do.
              </>
            ) : (
              <>
                <strong>Extension in progress.</strong>{' '}
                {pending.label
                  ? `Auto-destroy is moving to ${pending.label}.`
                  : 'Auto-destroy is being pushed out.'}{' '}
                The dates above still show the old value and update on their own
                within a few minutes.
              </>
            )}
          </Text>
        </div>
      )}
      {snackbar.open && (
        <div className={styles.snackbar}>
          <Alert
            status={
              snackbar.severity === 'error' ? 'danger' : snackbar.severity
            }
            title={snackbar.message}
            customActions={
              <Button
                size="small"
                variant="tertiary"
                onPress={() => setSnackbar(prev => ({ ...prev, open: false }))}
              >
                Dismiss
              </Button>
            }
          />
        </div>
      )}
    </>
  );
};
